// The component's own stylesheet, adopted into the shadow root as a single
// constructable CSSStyleSheet.
//
// ISOLATION (task-031 AC "no global style leakage in/out"): every rule lives in
// the shadow root, so nothing leaks OUT to the host page; and because shadow DOM
// does not inherit the page's element rules, almost nothing leaks IN. `all:
// initial` on :host is the belt-and-braces reset against inherited properties
// (font, color, line-height) that DO cross the boundary.
//
// THEME (F-037 / epic-005): the customer's BRAND_THEME arrives as CSS custom
// properties (--storadar-primary/secondary/font-family) and is set inline on the
// host at runtime. This sheet only supplies DEFAULTS via var() fallbacks, so a
// locator with no theme still looks deliberate — a restrained, spreadsheet-motif
// neutral palette (the DEFAULT only; a themed widget wears the customer's brand).
//
// DEFAULT PALETTE IS PINNED LIGHT (epic-007 PM follow-up #3): the DEFAULT
// (no BRAND_THEME) tokens below do NOT respond to `prefers-color-scheme` — a
// visitor's OS dark preference must not flip an un-themed widget dark on a
// customer's page, which is very often light-branded; the customer's brand
// always wins, never the visitor's OS. A themed widget's colors come entirely
// from --storadar-* (set inline by the host), so this pin only affects
// locators with no BRAND_THEME configured yet. There is currently no
// "customer opts into dark" signal other than the brand color variables
// themselves, so there is nothing here for a themed widget to opt back into —
// if that need arises, it should be a new BRAND_THEME field, not an OS media
// query.

/**
 * Builds the base stylesheet text. Kept as a function returning a string so the
 * (tiny) cost is paid once per document and the sheet is shared across instances.
 */
function baseCss(): string {
  return `
:host {
  /* Reset inherited props from the host page (font/color/line-height cross the
     shadow boundary); everything below re-establishes our own system. */
  all: initial;

  /* --- Default design tokens: spreadsheet-motif neutrals (DEFAULT theme only).
     Customer brand overrides land on --storadar-* and win via the fallbacks. */
  --sd-accent: var(--storadar-primary, #2f5cff);
  --sd-accent-ink: #ffffff;
  --sd-secondary: var(--storadar-secondary, #0b1220);
  --sd-font: var(--storadar-font-family, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif);

  --sd-surface: #ffffff;
  --sd-surface-2: #f7f8fa;
  --sd-ink: #16202c;
  --sd-ink-muted: #5b6672;
  --sd-line: #dfe3e8;      /* the faint spreadsheet gridline */
  --sd-line-strong: #c6ccd4;
  --sd-focus: #1f6feb;
  --sd-radius: 10px;
  --sd-shadow: 0 1px 2px rgba(16, 32, 44, 0.06), 0 8px 24px rgba(16, 32, 44, 0.08);

  display: block;
  position: relative;
  box-sizing: border-box;
  /* Reserved height => NO layout shift while async boot runs (AC). Configurable
     via the height attribute (mapped to this var) or --storadar-height. */
  height: var(--storadar-height, 520px);
  min-height: 320px;
  width: 100%;
  overflow: hidden;
  border: 1px solid var(--sd-line);
  border-radius: var(--sd-radius);
  background: var(--sd-surface);
  color: var(--sd-ink);
  font-family: var(--sd-font);
  font-size: 14px;
  line-height: 1.45;
  contain: content;
}

*, *::before, *::after { box-sizing: border-box; }

.root { display: flex; flex-direction: column; height: 100%; }

/* --- Shell: search bar header + the two-pane layout below it (F-032). ---
   position: relative anchors the task-033 detail panel overlay (inset:0) to the
   shell box rather than :host. */
.shell { display: flex; flex-direction: column; height: 100%; min-height: 0; position: relative; }

/* --- Two-pane layout: list rail + map. ---
   position: relative so the mobile absolute-positioned map/list-pane below
   resolve against THIS box, not :host — otherwise they'd overlay the search
   bar above them instead of stopping at this layout's own top edge. */
.layout {
  display: grid;
  grid-template-columns: minmax(260px, 34%) 1fr;
  /* Overridden from height:100% to flex:1 — the shell now has a search bar
     sibling above this, so the layout must share the shell's height rather than
     claim the full host height itself. */
  height: auto;
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
}

.list-pane {
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--sd-line);
  background: var(--sd-surface);
}

.map-pane { position: relative; min-height: 0; }
/* MapLibre adds its own .maplibregl-map (position: relative) to this very element,
   and its stylesheet is adopted AFTER ours (see mapChunk.adoptMapLibreCss). A bare
   .map-canvas rule ties on specificity and loses to the later sheet, so the
   container falls back to position:relative, its inset:0 stops applying, the
   absolutely-positioned GL canvas gives it no height, and the map collapses to 0px
   (blank pane). The descendant selector outranks MapLibre's single class so our
   fill always wins, regardless of adoption order. */
.map-pane > .map-canvas { position: absolute; inset: 0; }

/* The map failed to load (F-030 progressive enhancement): the directory beside it is fully
   usable, so the failure is confined to the pane that actually failed instead of replacing the
   whole widget. Muted, centred, with one real action — it must read as "this panel is missing",
   not as an alarm about a locator that is working. */
.map-error {
  position: absolute; inset: 0; z-index: 1;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 10px; padding: 16px; text-align: center;
  background: var(--sd-surface-2); color: var(--sd-ink-muted);
}
.map-error-text { margin: 0; font-size: 13px; max-width: 34ch; }

/* --- List header + aria-live count. --- */
.list-head {
  padding: 12px 14px;
  border-bottom: 1px solid var(--sd-line);
  background: var(--sd-surface-2);
}
.list-title { font-weight: 600; font-size: 14px; margin: 0; color: var(--sd-ink); }
.list-count { margin: 2px 0 0; font-size: 12px; color: var(--sd-ink-muted); }

.list-scroll { overflow-y: auto; flex: 1 1 auto; min-height: 0; }
.list { list-style: none; margin: 0; padding: 0; }

.list-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 14px;
  min-height: 44px;               /* touch target */
  border-bottom: 1px solid var(--sd-line);
  cursor: pointer;
  background: var(--sd-surface);
  transition: background-color 120ms ease;
}
.list-item:hover, .list-item.is-active { background: var(--sd-surface-2); }
.list-item.is-active { box-shadow: inset 3px 0 0 var(--sd-accent); }
.list-item:focus-visible { outline: 2px solid var(--sd-focus); outline-offset: -2px; }
.list-name { font-weight: 600; color: var(--sd-ink); }
/* Directory address line (widget-directory-list): secondary to the title. */
.list-address { font-size: 13px; color: var(--sd-ink-muted); }
/*
 * Directory description: server-truncated to ~160 chars, and clamped to two lines
 * as a belt-and-braces guard so an untruncated/edge-case value never blows out the
 * card height. -webkit-line-clamp is the broadly-supported multi-line ellipsis.
 */
.list-description {
  font-size: 13px;
  color: var(--sd-ink-muted);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.list-meta { font-size: 12px; color: var(--sd-ink-muted); }

/* F-032 "no results within radius" row: not a normal, selectable pin row. */
.list-item-message { cursor: default; align-items: flex-start; gap: 8px; }
.list-widen-button {
  align-self: flex-start;
  min-height: 32px; padding: 6px 12px;
  border: 1px solid var(--sd-line-strong); border-radius: 8px;
  background: var(--sd-accent); color: var(--sd-accent-ink);
  font: 600 12px/1 var(--sd-font); cursor: pointer;
}
.list-widen-button:focus-visible { outline: 2px solid var(--sd-focus); outline-offset: 2px; }

/* --- Search bar (task-032, F-032): search box + autocomplete dropdown, "use my
   location", radius, and search-as-map-moves. Sits above the layout. --- */
.search-bar {
  display: flex; flex-direction: column; gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--sd-line);
  background: var(--sd-surface-2);
}
.search-row { display: flex; gap: 8px; align-items: stretch; }
.search-combo { position: relative; flex: 1 1 auto; min-width: 0; }
.search-input {
  width: 100%; min-height: 40px; padding: 0 12px;
  border: 1px solid var(--sd-line-strong); border-radius: 8px;
  background: var(--sd-surface); color: var(--sd-ink);
  font: 400 14px/1 var(--sd-font);
}
.search-input:focus-visible { outline: 2px solid var(--sd-focus); outline-offset: 1px; }
.search-listbox {
  position: absolute; left: 0; right: 0; top: calc(100% + 4px); z-index: 4;
  margin: 0; padding: 4px; list-style: none; max-height: 240px; overflow-y: auto;
  border: 1px solid var(--sd-line-strong); border-radius: 8px;
  background: var(--sd-surface); box-shadow: var(--sd-shadow);
}
.search-option {
  padding: 8px 10px; border-radius: 6px; cursor: pointer;
  font-size: 13px; color: var(--sd-ink);
}
.search-option.is-active, .search-option:hover { background: var(--sd-surface-2); }
.search-status { margin: 0; font-size: 12px; color: var(--sd-ink-muted); }
.search-geo-button {
  flex: 0 0 auto; min-height: 40px; padding: 0 12px;
  border: 1px solid var(--sd-line-strong); border-radius: 8px;
  background: var(--sd-surface); color: var(--sd-ink);
  font: 600 12px/1 var(--sd-font); cursor: pointer; white-space: nowrap;
}
.search-geo-button:hover { background: var(--sd-surface); border-color: var(--sd-accent); }
.search-geo-button:focus-visible { outline: 2px solid var(--sd-focus); outline-offset: 2px; }
.search-geo-button:disabled { opacity: 0.6; cursor: default; }
.search-controls { display: flex; flex-wrap: wrap; gap: 14px; align-items: center; }
.search-radius-label {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; color: var(--sd-ink-muted);
}
.search-radius {
  min-height: 32px; padding: 0 8px;
  border: 1px solid var(--sd-line-strong); border-radius: 6px;
  background: var(--sd-surface); color: var(--sd-ink);
  font: 400 12px/1 var(--sd-font);
}

/* --- Filter bar (task-033, F-018): tag chips + open-now toggle + clear all.
   Sits between the search bar and the layout. --- */
.filter-bar {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--sd-line);
  background: var(--sd-surface-2);
}
.filter-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.chip {
  display: inline-flex; align-items: center; gap: 6px;
  min-height: 32px; padding: 4px 12px;
  border: 1px solid var(--sd-line-strong); border-radius: 999px;
  background: var(--sd-surface); color: var(--sd-ink);
  font: 500 12px/1 var(--sd-font); cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.chip:hover { border-color: var(--sd-accent); }
.chip:focus-visible { outline: 2px solid var(--sd-focus); outline-offset: 2px; }
.chip.is-selected { background: var(--sd-accent); color: var(--sd-accent-ink); border-color: var(--sd-accent); }
/* A tag with zero matches in view is dimmed but still visible (honest state). */
.chip.is-empty:not(.is-selected) { opacity: 0.45; }
.chip:disabled { opacity: 0.45; cursor: default; }
.chip-count {
  padding: 0 6px; border-radius: 999px;
  background: var(--sd-surface-2); color: var(--sd-ink-muted);
  font-size: 11px;
}
.chip-count:empty { display: none; }
.chip.is-selected .chip-count { background: rgba(255, 255, 255, 0.22); color: var(--sd-accent-ink); }
.filter-clear {
  margin-left: auto; min-height: 32px; padding: 4px 12px;
  border: none; background: none; color: var(--sd-ink-muted);
  font: 600 12px/1 var(--sd-font); cursor: pointer; text-decoration: underline;
}
.filter-clear:focus-visible { outline: 2px solid var(--sd-focus); outline-offset: 2px; }
.filter-clear[hidden] { display: none; }

/* --- Location detail panel (task-033): a dialog overlay over the shell. --- */
.detail-overlay {
  position: absolute; inset: 0; z-index: 6;
  display: flex; align-items: stretch; justify-content: flex-end;
  background: rgba(16, 32, 44, 0.32);
}
.detail-overlay[hidden] { display: none; }
.detail-panel {
  display: flex; flex-direction: column;
  width: min(420px, 100%); height: 100%;
  background: var(--sd-surface); color: var(--sd-ink);
  box-shadow: var(--sd-shadow);
  overflow: hidden;
}
.detail-header {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
  padding: 14px;
  border-bottom: 1px solid var(--sd-line);
  background: var(--sd-surface-2);
}
.detail-title { margin: 0; font-size: 16px; font-weight: 600; color: var(--sd-ink); }
.detail-close {
  flex: 0 0 auto; min-height: 32px; padding: 4px 12px;
  border: 1px solid var(--sd-line-strong); border-radius: 8px;
  background: var(--sd-surface); color: var(--sd-ink);
  font: 600 12px/1 var(--sd-font); cursor: pointer;
}
.detail-close:focus-visible { outline: 2px solid var(--sd-focus); outline-offset: 2px; }
.detail-body {
  flex: 1 1 auto; min-height: 0; overflow-y: auto;
  padding: 14px; display: flex; flex-direction: column; gap: 14px;
}
.detail-section { display: flex; flex-direction: column; gap: 6px; }
.detail-heading { margin: 0; font-size: 12px; font-weight: 600; letter-spacing: 0.02em; text-transform: uppercase; color: var(--sd-ink-muted); }
.detail-address { margin: 0; font-size: 13px; color: var(--sd-ink-muted); }
.detail-description { margin: 0; font-size: 13px; color: var(--sd-ink); }

.detail-badge { align-self: flex-start; padding: 4px 10px; border-radius: 999px; font: 600 12px/1.3 var(--sd-font); }
.detail-badge.is-open { background: #e6f4ea; color: #1a7f37; }
.detail-badge.is-closed { background: #fce8e6; color: #b3261e; }
.detail-badge.is-unknown { background: var(--sd-surface-2); color: var(--sd-ink-muted); }

.detail-contact { display: flex; flex-direction: column; gap: 4px; }
.detail-link { color: var(--sd-accent); text-decoration: none; font-size: 13px; word-break: break-word; }
.detail-link:hover { text-decoration: underline; }
.detail-link:focus-visible { outline: 2px solid var(--sd-focus); outline-offset: 2px; }

.detail-hours { width: 100%; border-collapse: collapse; font-size: 13px; }
.detail-hours th { text-align: left; font-weight: 400; color: var(--sd-ink-muted); padding: 3px 12px 3px 0; white-space: nowrap; }
.detail-hours td { text-align: right; color: var(--sd-ink); padding: 3px 0; }
.detail-hours tr.is-today th, .detail-hours tr.is-today td { font-weight: 700; color: var(--sd-ink); }

.detail-fields { display: flex; flex-direction: column; gap: 6px; }
.detail-field { display: flex; justify-content: space-between; gap: 12px; font-size: 13px; }
.detail-field-label { color: var(--sd-ink-muted); }
.detail-field-value { color: var(--sd-ink); text-align: right; }

.detail-images { display: flex; gap: 8px; overflow-x: auto; }
.detail-image { flex: 0 0 auto; width: 120px; height: 80px; object-fit: cover; border-radius: 8px; border: 1px solid var(--sd-line); background: var(--sd-surface-2); }

.detail-directions { display: flex; flex-wrap: wrap; gap: 8px; }
.detail-direction {
  display: inline-flex; align-items: center; min-height: 36px; padding: 6px 14px;
  border: 1px solid var(--sd-line-strong); border-radius: 8px;
  background: var(--sd-surface); color: var(--sd-ink);
  font: 600 12px/1 var(--sd-font); text-decoration: none;
}
.detail-direction:hover { border-color: var(--sd-accent); }
.detail-direction:focus-visible { outline: 2px solid var(--sd-focus); outline-offset: 2px; }

.detail-loading, .detail-error-body { margin: 0; font-size: 13px; color: var(--sd-ink-muted); }
.detail-error { display: flex; flex-direction: column; gap: 10px; }
.detail-retry {
  align-self: flex-start; min-height: 36px; padding: 6px 14px;
  border: 1px solid var(--sd-line-strong); border-radius: 8px;
  background: var(--sd-accent); color: var(--sd-accent-ink);
  font: 600 12px/1 var(--sd-font); cursor: pointer;
}
.detail-retry:focus-visible { outline: 2px solid var(--sd-focus); outline-offset: 2px; }

/* --- Map markers (rendered by the lazy map chunk, styled here in the root). --- */
.storadar-cluster {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 34px; height: 34px; padding: 0 8px;
  border: 2px solid var(--sd-surface);
  border-radius: 999px;
  background: var(--sd-accent);
  color: var(--sd-accent-ink);
  font: 600 12px/1 var(--sd-font);
  cursor: pointer;
  box-shadow: var(--sd-shadow);
}
.storadar-cluster.is-large { min-width: 44px; height: 44px; font-size: 13px; }

.storadar-pin {
  width: 18px; height: 18px; padding: 0;
  border: 3px solid var(--sd-surface);
  border-radius: 50%;
  background: var(--sd-accent);
  cursor: pointer;
  box-shadow: var(--sd-shadow);
  transition: transform 120ms ease;
}
.storadar-pin.is-active { transform: scale(1.35); background: var(--sd-secondary); }

/* --- Skeleton (async boot placeholder; occupies the reserved box). --- */
.skeleton { display: grid; grid-template-columns: minmax(260px, 34%) 1fr; height: 100%; }
.skeleton-list { border-right: 1px solid var(--sd-line); padding: 14px; }
.skeleton-row {
  height: 40px; margin-bottom: 10px; border-radius: 6px;
  background: linear-gradient(90deg, var(--sd-surface-2) 25%, var(--sd-line) 37%, var(--sd-surface-2) 63%);
  background-size: 400% 100%;
  animation: sd-shimmer 1.4s ease infinite;
}
.skeleton-map { background: var(--sd-surface-2); }
@keyframes sd-shimmer { 0% { background-position: 100% 0; } 100% { background-position: 0 0; } }

/* --- Centered message states (error / empty / unavailable). --- */
.state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 10px; height: 100%; padding: 32px; text-align: center;
}
.state-title { margin: 0; font-size: 15px; font-weight: 600; color: var(--sd-ink); }
.state-body { margin: 0; max-width: 42ch; font-size: 13px; color: var(--sd-ink-muted); }
.state-action {
  margin-top: 4px; min-height: 44px; padding: 8px 18px;
  border: 1px solid var(--sd-line-strong); border-radius: 8px;
  background: var(--sd-accent); color: var(--sd-accent-ink);
  font: 600 13px/1 var(--sd-font); cursor: pointer;
}
.state-action:focus-visible { outline: 2px solid var(--sd-focus); outline-offset: 2px; }

/* Disclosure line (e.g. "N hidden — hours unavailable"): honest, not hidden. */
.disclosure { padding: 8px 14px; font-size: 12px; color: var(--sd-ink-muted); border-top: 1px solid var(--sd-line); background: var(--sd-surface-2); }

/* --- "Powered by Storadar" badge (task-034, F-107). The slot PROJECTS the
   light-DOM anchor the embed snippet ships (crawlable pre-JS); ::slotted styles
   it in place. .badge is the fallback anchor built locally when nothing is
   slotted (StoradarMap.buildFallbackBadge) - slot fallback content is never
   matched by ::slotted, so it needs its own identical rule. Suppressed
   (slot[hidden]) only when the SERVER config says badge_hidden (SCALE-only).
   Placed bottom-LEFT to avoid overlapping MapLibre's compact attribution
   control, which sits bottom-right. --- */
.badge-slot { position: absolute; left: 8px; bottom: 8px; z-index: 2; display: block; }
.badge-slot[hidden] { display: none; }
::slotted(a[slot="badge"]), .badge {
  padding: 4px 8px; border-radius: 6px;
  background: color-mix(in srgb, var(--sd-surface) 88%, transparent);
  border: 1px solid var(--sd-line);
  /* Slotted content inherits from the HOST PAGE's cascade, not the shadow
     tree's, so every text-affecting property here must be explicit rather
     than relied on via inheritance (unlike the rest of this sheet). */
  font: 11px var(--sd-font);
  color: var(--sd-ink-muted);
  text-decoration: none;
}

/* --- Mobile: bottom-sheet layout under the container breakpoint. Driven by a
   ResizeObserver class toggle (container queries aren't universally available on
   a shadow host across the browser matrix, and ResizeObserver is shadow-safe). */
:host(.is-mobile) .layout { grid-template-columns: 1fr; }
:host(.is-mobile) .map-pane { position: absolute; inset: 0; }
:host(.is-mobile) .list-pane {
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 3;
  height: 44%; max-height: 60%;
  border-right: none; border-top: 1px solid var(--sd-line-strong);
  border-radius: 14px 14px 0 0;
  box-shadow: 0 -8px 24px rgba(16, 32, 44, 0.14);
}
:host(.is-mobile) .list-head { border-radius: 14px 14px 0 0; }
/* Detail panel becomes a bottom sheet on narrow embeds (task-033). */
:host(.is-mobile) .detail-overlay { align-items: flex-end; justify-content: stretch; }
:host(.is-mobile) .detail-panel { width: 100%; height: 82%; max-height: 90%; border-radius: 14px 14px 0 0; }
/* Grab handle affordance for the sheet. */
:host(.is-mobile) .list-head::before {
  content: ""; display: block; width: 36px; height: 4px; margin: 0 auto 8px;
  border-radius: 999px; background: var(--sd-line-strong);
}

/* NOTE: the open/closed badge tints above used to have an OS-dark-mode variant
   here; removed with the rest of the OS-driven dark mode (epic-007 PM
   follow-up #3, see the class-level kdoc) — with --sd-surface now pinned
   light for the DEFAULT theme, a badge tint keyed to prefers-color-scheme
   would read against a surface that never actually goes dark, which is worse
   than the plain light tint it would "fix". */

@media (prefers-reduced-motion: reduce) {
  .skeleton-row { animation: none; }
  .list-item, .storadar-pin, .chip { transition: none; }
}
`;
}

/**
 * Returns the shared base stylesheet, constructed once. Callers adopt it into
 * their shadow root: `shadow.adoptedStyleSheets = [baseStyleSheet()]`.
 */
let cached: CSSStyleSheet | null = null;
export function baseStyleSheet(): CSSStyleSheet {
  if (cached == null) {
    cached = new CSSStyleSheet();
    cached.replaceSync(baseCss());
  }
  return cached;
}
